/**
 * Caixas de fusíveis do projeto.
 *
 * Três níveis: a **caixa**, seus **elementos** (F19, F20 — cada fusível ou
 * relé) e as **funções** de cada um (que circuito e pino aquele elemento
 * alimenta). A tela desce esses três níveis da esquerda para a direita, porque é
 * assim que se procura: acho a caixa, acho o fusível, vejo o que ele alimenta.
 *
 * **A lista agrupa por título, e isso não é enfeite.** A mesma caixa física
 * existe uma vez por modelo: no projeto 550 são **120 caixas com 7 títulos**.
 * Lista plana daria 120 linhas repetidas que ninguém distingue — agrupada, dá
 * sete, com a contagem de modelos de cada uma.
 */

import { useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import {
  type CaixaDeFusiveis, type ElementoDaCaixa, usarCaixasDoProjeto, usarElementosDaCaixa, usarExcluirElemento, usarFuncoesDoElemento, usarPublicarCaixa, usarSalvarElemento,
} from "@/api/caixasFusiveis"
import { usarModelosDoProjeto } from "@/api/modelos"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { FormularioDaCaixa } from "./FormularioDaCaixa"
import { ImportarFusiveis, RegenerarCaixa } from "./RegenerarCaixa"

export function CaixasDeFusiveis() {
  const { projetoId } = useParams()
  const id = Number(projetoId)

  const caixas = usarCaixasDoProjeto(id)
  const modelos = usarModelosDoProjeto(id)
  const [caixaAberta, setCaixaAberta] = useState<CaixaDeFusiveis | null>(null)
  const [elementoAberto, setElementoAberto] = useState<ElementoDaCaixa | null>(null)
  const [gruposAbertos, setGruposAbertos] = useState<Set<string>>(new Set())
  /** `null` = fechado; `"nova"` = criando; uma caixa = alterando aquela. */
  const [emEdicao, setEmEdicao] = useState<CaixaDeFusiveis | "nova" | null>(null)
  const [recado, setRecado] = useState<string | null>(null)

  const nomeDoModelo = useMemo(() => {
    const m = new Map<number, string>()
    for (const x of modelos.data ?? []) m.set(x.id, x.descricao)
    return m
  }, [modelos.data])

  /** Agrupado por título: 120 caixas viram 7 grupos. */
  const grupos = useMemo(() => {
    const mapa = new Map<string, CaixaDeFusiveis[]>()
    for (const c of caixas.data ?? []) {
      const t = (c.titulo ?? "").trim() || "(sem título)"
      const lista = mapa.get(t) ?? []
      lista.push(c)
      mapa.set(t, lista)
    }
    return [...mapa.entries()].sort((a, b) => b[1].length - a[1].length)
  }, [caixas.data])

  const total = (caixas.data ?? []).length

  function alternarGrupo(titulo: string) {
    setGruposAbertos((s) => {
      const n = new Set(s)
      n.has(titulo) ? n.delete(titulo) : n.add(titulo)
      return n
    })
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        {/* nível 1 — as caixas, agrupadas por título */}
        <div style={{
          flex: "0 0 320px", width: 320, minWidth: 0, overflowY: "auto",
          borderRight: "1px solid var(--line2)", padding: 14,
        }}>
          <button
            onClick={() => setEmEdicao(emEdicao === "nova" ? null : "nova")}
            aria-pressed={emEdicao === "nova"}
            style={{
              width: "100%", marginBottom: 10, padding: "6px 10px", borderRadius: 7,
              fontSize: 12, fontWeight: 600, cursor: "pointer",
              border: "1px solid var(--acao)",
              background: emEdicao === "nova" ? "var(--acao-soft)" : "var(--acao)",
              color: emEdicao === "nova" ? "var(--acao-on-soft)" : "var(--acao-ink)",
            }}
          >
            {emEdicao === "nova" ? "Cancelar" : "+ Nova caixa"}
          </button>

          {caixas.isPending ? (
            <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando as caixas…</p>
          ) : caixas.error ? (
            <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
              {caixas.error instanceof ErroDaApi
                ? caixas.error.message : "Não foi possível ler as caixas."}
            </p>
          ) : total === 0 ? (
            <p style={{ fontSize: 12.5, color: "var(--txt3)", lineHeight: 1.6 }}>
              <b>Este projeto não tem caixa de fusíveis.</b><br />
              Nem todo projeto tem — é conteúdo próprio, cadastrado à parte do pinout.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {grupos.map(([titulo, doGrupo]) => (
                <section key={titulo} style={{
                  border: "1px solid var(--line2)", borderRadius: 9, overflow: "hidden",
                }}>
                  <button
                    onClick={() => alternarGrupo(titulo)}
                    aria-expanded={gruposAbertos.has(titulo)}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 7, width: "100%",
                      padding: "9px 11px", textAlign: "left", background: "var(--surface3)",
                    }}
                  >
                    <span aria-hidden="true" style={{ fontSize: 10, color: "var(--txt3)", marginTop: 2 }}>
                      {gruposAbertos.has(titulo) ? "▾" : "▸"}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.35 }}>{titulo}</span>
                      <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>
                        {doGrupo.length} {doGrupo.length === 1 ? "modelo" : "modelos"}
                      </span>
                    </span>
                  </button>

                  {gruposAbertos.has(titulo) && doGrupo.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setCaixaAberta(c); setElementoAberto(null) }}
                      aria-pressed={caixaAberta?.id === c.id}
                      style={{
                        display: "flex", alignItems: "center", gap: 8, width: "100%",
                        padding: "7px 11px 7px 25px", textAlign: "left",
                        borderTop: "1px solid var(--line)",
                        background: caixaAberta?.id === c.id ? "var(--brand-soft)" : "var(--surface)",
                        color: caixaAberta?.id === c.id ? "var(--brand-on-soft)" : "var(--txt2)",
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, lineHeight: 1.35 }}>
                        {c.modelos.length === 0
                          ? <span style={{ color: "var(--txt3)" }}>sem modelo</span>
                          : c.modelos.map((m) => nomeDoModelo.get(m) ?? `modelo ${m}`).join(", ")}
                      </span>
                      {c.publicado && (
                        <span title="Publicado" aria-label="Publicado" style={{
                          flex: "0 0 6px", width: 6, height: 6, borderRadius: "50%",
                          background: "var(--ok)",
                        }} />
                      )}
                    </button>
                  ))}
                </section>
              ))}
            </div>
          )}
        </div>

        {/* nível 2 — os elementos da caixa escolhida */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {!caixaAberta ? (
            <p style={{ margin: 18, fontSize: 13, color: "var(--txt2)", maxWidth: "50ch", lineHeight: 1.6 }}>
              Escolha uma caixa à esquerda.
              <br />
              <span style={{ fontSize: 12, color: "var(--txt3)" }}>
                Cada título é a mesma caixa física, repetida uma vez por modelo — abra o grupo para
                ver os modelos.
              </span>
            </p>
          ) : (
            <>
            <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 9,
                          padding: "8px 14px", borderBottom: "1px solid var(--line2)" }}>
              <b style={{ fontSize: 12.5, minWidth: 0, overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {caixaAberta.titulo || "(sem título)"}
              </b>
              <button
                onClick={() => setEmEdicao(
                  emEdicao !== "nova" && emEdicao?.id === caixaAberta.id ? null : caixaAberta)}
                style={{
                  marginLeft: "auto", padding: "3px 10px", borderRadius: 6, fontSize: 11.5,
                  cursor: "pointer", border: "1px solid var(--line)",
                  background: "var(--surface)", color: "var(--txt)",
                }}
              >
                {emEdicao !== "nova" && emEdicao?.id === caixaAberta.id ? "Fechar" : "Alterar a caixa"}
              </button>
            </div>

            <Elementos
              projetoId={id}
              caixa={caixaAberta}
              modelos={caixaAberta.modelos.map((m) => nomeDoModelo.get(m) ?? `modelo ${m}`)}
              elementoAberto={elementoAberto}
              aoAbrirElemento={setElementoAberto}
            />
            </>
          )}
        </div>
      </div>

      {emEdicao !== null && (
        <FormularioDaCaixa
          projetoId={id}
          caixa={emEdicao === "nova" ? null : emEdicao}
          aoFechar={() => setEmEdicao(null)}
          aoAvisar={setRecado}
        />
      )}

      <BarraDeEstado>
        {total > 0 && (
          <span>
            <b className="num" style={{ color: "var(--txt)" }}>{total}</b>{" "}
            {total === 1 ? "caixa" : "caixas"} em{" "}
            <b className="num" style={{ color: "var(--txt)" }}>{grupos.length}</b>{" "}
            {grupos.length === 1 ? "título" : "títulos"}
          </span>
        )}
        <span style={{ color: "var(--txt3)" }}>
          {recado ?? "A mesma caixa existe uma vez por modelo — por isso a lista agrupa."}
        </span>
      </BarraDeEstado>
    </div>
  )
}

function Elementos({ projetoId, caixa, modelos, elementoAberto, aoAbrirElemento }: {
  projetoId: number
  caixa: CaixaDeFusiveis
  modelos: string[]
  elementoAberto: ElementoDaCaixa | null
  aoAbrirElemento: (e: ElementoDaCaixa | null) => void
}) {
  // As duas operacoes em massa moram atras de um botao: elas apagam ou
  // acrescentam a tabela inteira, e nao devem ficar no caminho de quem so quer
  // olhar um fusivel.
  const [ferramentas, setFerramentas] = useState(false)
  const aoAlternarFerramentas = () => setFerramentas((v) => !v)
  const elementos = usarElementosDaCaixa(caixa.id)
  const salvar = usarSalvarElemento(projetoId, caixa.id)
  const excluir = usarExcluirElemento(projetoId, caixa.id)
  const publicar = usarPublicarCaixa(projetoId)
  const [confirmando, setConfirmando] = useState<number | null>(null)
  const [recadoDaPublicacao, setRecadoDaPublicacao] = useState<string | null>(null)

  return (
    <>
      <div
        className="barra"
        style={{
          flex: "0 0 auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          padding: "10px 18px", background: "var(--surface)", borderBottom: "1px solid var(--line)",
        }}
      >
        <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>{caixa.titulo}</span>
          <span style={{ fontSize: 11, color: "var(--txt3)" }}>
            {modelos.length > 0 ? modelos.join(", ") : "sem modelo"}
          </span>
        </span>

        {/* Publicar e o que faz a caixa aparecer para quem paga, e cadastrar sem
            publicar deixa o trabalho pronto e invisivel -- sem nenhuma tela
            dizendo. Por isso o estado fica escrito, e nao so um ponto ao lado do
            titulo. Tirar do ar nao apaga nada. */}
        <span style={{
          display: "flex", alignItems: "center", gap: 7, padding: "3px 9px",
          borderRadius: 6, fontSize: 11,
          border: "1px solid " + (caixa.publicado ? "var(--ok, #2e7d32)" : "var(--line)"),
        }}>
          <span style={{
            fontWeight: 600,
            color: caixa.publicado ? "var(--ok, #2e7d32)" : "var(--txt3)",
          }}>
            {caixa.publicado ? "● no ar" : "○ fora do ar"}
          </span>
          <button
            onClick={() => {
              setRecadoDaPublicacao(null)
              publicar.mutate(
                { id: caixa.id, publicar: !caixa.publicado },
                {
                  onSuccess: () => setRecadoDaPublicacao(
                    caixa.publicado ? "Caixa fora do ar." : "Caixa no ar."),
                  onError: (e) => setRecadoDaPublicacao(
                    e instanceof ErroDeTrava || e instanceof ErroDaApi
                      ? e.message : "Não foi possível trocar."),
                })
            }}
            disabled={publicar.isPending}
            title={caixa.publicado
              ? "Tira do ar sem apagar nada: a caixa, os pinos e as tabelas continuam."
              : "Faz a caixa aparecer para quem paga."}
            style={{
              border: 0, background: "transparent", cursor: "pointer", padding: 0,
              fontSize: 10.5, color: "var(--txt2)", textDecoration: "underline",
            }}
          >
            {publicar.isPending ? "…" : caixa.publicado ? "tirar do ar" : "publicar"}
          </button>
        </span>

        {recadoDaPublicacao && (
          <span style={{ fontSize: 11, color: "var(--txt2)", maxWidth: "50ch", lineHeight: 1.45 }}>
            {recadoDaPublicacao}
          </span>
        )}

        <span style={{ marginLeft: "auto" }} />
        <button
          onClick={() => aoAlternarFerramentas()}
          style={{
            height: 28, padding: "0 11px", borderRadius: 7, fontSize: 12.5,
            border: "1px solid var(--line)", background: "var(--surface)", color: "var(--txt)",
          }}
        >
          Refazer / importar
        </button>
        <button
          onClick={() => salvar.mutate({
            id: null,
            dados: { nomeCiclo: "", nomeFabricante: "", descricao: "", obs: "", exibir: true },
          })}
          disabled={salvar.isPending}
          style={{
            height: 28, padding: "0 11px", borderRadius: 7, background: "var(--brand)",
            color: "#FFFFFF", fontSize: 12.5, fontWeight: 600,
          }}
        >
          Novo elemento
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 14 }}>
        {ferramentas && (
          <div style={{ marginBottom: 16, paddingBottom: 8, borderBottom: "1px solid var(--line2)" }}>
            <RegenerarCaixa projetoId={projetoId} caixaId={caixa.id} titulo={caixa.titulo} />
            <ImportarFusiveis projetoId={projetoId} caixaId={caixa.id} titulo={caixa.titulo} />
            <ImportarFusiveis projetoId={projetoId} caixaId={null} titulo={caixa.titulo} />
          </div>
        )}

        {elementos.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando os elementos…</p>
        ) : (elementos.data ?? []).length === 0 ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Esta caixa ainda não tem elemento.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {(elementos.data ?? []).map((el) => (
              <div key={el.id} style={{
                border: `1px solid ${elementoAberto?.id === el.id ? "var(--brand)" : "var(--line2)"}`,
                borderRadius: 9, background: "var(--surface)", overflow: "hidden",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 11px" }}>
                  <span className="mono" style={{
                    flex: "0 0 auto", padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                    background: "var(--surface3)",
                  }}>
                    {el.nomeCiclo || el.nomeFabricante || "—"}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
                    {el.descricao || <span style={{ color: "var(--txt3)" }}>sem descrição</span>}
                    {el.obs && (
                      <span style={{ display: "block", fontSize: 10.5, color: "var(--txt3)" }}>{el.obs}</span>
                    )}
                  </span>
                  {!el.exibir && (
                    <span title="Não aparece para o cliente" style={{
                      fontSize: 10.5, fontWeight: 600, color: "var(--txt3)",
                    }}>
                      oculto
                    </span>
                  )}
                  <button
                    onClick={() => aoAbrirElemento(elementoAberto?.id === el.id ? null : el)}
                    aria-expanded={elementoAberto?.id === el.id}
                    style={{
                      height: 24, padding: "0 9px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                      border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--txt2)",
                    }}
                  >
                    {elementoAberto?.id === el.id ? "fechar" : "o que alimenta"}
                  </button>
                  {confirmando === el.id ? (
                    <button
                      onClick={() => excluir.mutate(el.id, { onSuccess: () => setConfirmando(null) })}
                      style={{
                        height: 24, padding: "0 9px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                        background: "var(--erro)", color: "#FFFFFF",
                      }}
                    >
                      confirmar
                    </button>
                  ) : (
                    <button onClick={() => setConfirmando(el.id)}
                            style={{ fontSize: 11, color: "var(--erro)" }}>
                      excluir
                    </button>
                  )}
                </div>

                {elementoAberto?.id === el.id && (
                  <Funcoes caixaId={caixa.id} elemento={el} />
                )}
              </div>
            ))}
          </div>
        )}

        {(salvar.error ?? excluir.error) != null && (
          <Erro erro={salvar.error ?? excluir.error} />
        )}
      </div>
    </>
  )
}

/**
 * Nível 3: o que aquele fusível alimenta.
 *
 * **Só leitura, e de propósito.** As funções vêm do pinout — o campo `origem` é
 * um `pinout_id` — e editá-las à mão aqui desencontraria as duas pontas: a
 * tabela diria uma coisa e o pinout, outra. Quem corrige a função corrige o
 * pinout, e ela acompanha.
 */
function Funcoes({ caixaId, elemento }: {
  caixaId: number
  elemento: ElementoDaCaixa
}) {
  const funcoes = usarFuncoesDoElemento(caixaId, elemento.id)

  return (
    <div style={{ padding: "0 11px 11px 11px", borderTop: "1px solid var(--line)" }}>
      {funcoes.isPending ? (
        <p style={{ margin: "9px 0 0", fontSize: 11.5, color: "var(--txt2)" }}>Carregando…</p>
      ) : (funcoes.data ?? []).length === 0 ? (
        <p style={{ margin: "9px 0 0", fontSize: 11.5, color: "var(--txt3)" }}>
          Este elemento não tem função registrada — não se sabe o que ele alimenta.
        </p>
      ) : (
        <div style={{ marginTop: 9, overflowX: "auto" }}>
          <table style={{ fontSize: 11.5, minWidth: 420 }}>
            <thead>
              <tr style={{ color: "var(--txt2)" }}>
                <th style={{ padding: "5px 8px", textAlign: "left", fontSize: 10.5 }}>CIRCUITO</th>
                <th style={{ padding: "5px 8px", textAlign: "left", fontSize: 10.5 }}>PINO</th>
                <th style={{ padding: "5px 8px", textAlign: "left", fontSize: 10.5 }}>FUNÇÃO</th>
                <th style={{ padding: "5px 8px", textAlign: "left", fontSize: 10.5 }}>OBS</th>
              </tr>
            </thead>
            <tbody>
              {(funcoes.data ?? []).map((f) => (
                <tr key={f.id} style={{ borderTop: "1px solid var(--line)" }}>
                  <td className="mono" style={{ padding: "5px 8px" }}>{f.circuito || "—"}</td>
                  <td className="mono" style={{ padding: "5px 8px" }}>{f.pino || "—"}</td>
                  <td style={{ padding: "5px 8px" }}>
                    {f.funcao || <span style={{ color: "var(--txt3)" }}>—</span>}
                  </td>
                  <td style={{ padding: "5px 8px", color: "var(--txt3)" }}>{f.obs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Erro({ erro }: { erro: unknown }) {
  return (
    <div style={{
      marginTop: 12, padding: "10px 12px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
      background: erro instanceof ErroDeTrava ? "var(--acao-soft)" : "var(--erro-soft)",
      color: erro instanceof ErroDeTrava ? "var(--acao-on-soft)" : "var(--erro-on-soft)",
    }}>
      {erro instanceof ErroDaApi || erro instanceof Error ? erro.message : "Não foi possível."}
    </div>
  )
}
